import React from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { Coins, Clock, TrendingDown, TrendingUp, CheckCircle, Percent } from 'lucide-react'
import { AnalyticsLayout } from '@/components/analytics/AnalyticsLayout'
import { KPICard, KPICardData, KPICardSkeleton } from '@/components/shared/KPICard'
import { ChartChrome } from '@/components/shared/ChartChrome'
import {
  AnalyticsEmptyState,
  AnalyticsErrorBlock,
  AnalyticsLoadingBlock,
} from '@/components/shared/AnalyticsStates'
import { ExportButton } from '@/components/shared/ExportButton'
import { useAnalyticsQuery } from '@/hooks/useAnalyticsQuery'
import { useAnalyticsFilters } from '@/contexts/AnalyticsFiltersContext'
import { useFilterDispatch } from '@/contexts/useFilterDispatch'
import {
  HeatmapLeafletMap,
  HeatmapPoint,
} from '@/components/analytics/HeatmapLeafletMap'
import { euroInt, int, pct } from '@/lib/format'

// ---------------- response shapes ----------------

interface MarketSummary {
  medianSqmPrice: number
  medianTimeOnMarket: number
  priceDropsLast30d: number
  priceIncreasesLast30d: number
  salesLast30d: number
  avgListToSaleDiscountPct: number
}

interface PriceByCityRow {
  city: string
  avgSqmPrice: number
  medianSqmPrice: number
  units: number
}

interface PriceTrendSeries {
  city: string
  points: Array<{ month: string; avgSqmPrice: number; count: number }>
}

interface PriceDistributionResponse {
  buckets: Array<{ lower: number; upper: number; count: number }>
  min: number
  max: number
  median: number
  q1: number
  q3: number
  totalUnits: number
  trimmedCount: number
}

interface TypologyMixRow {
  typology: string
  units: number
  pct: number
}

// ---------------- constants ----------------

const TYPOLOGY_COLORS = [
  '#2563eb',
  '#16a34a',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#0891b2',
  '#db2777',
  '#64748b',
  '#059669',
  '#dc2626',
]

const TREND_COLORS = ['#2563eb', '#16a34a', '#f59e0b', '#ef4444', '#8b5cf6']

// ---------------- sections ----------------

function SummaryRow() {
  const { data, isLoading, isError, refetch } = useAnalyticsQuery<MarketSummary>(
    'market-summary',
    '/analytics/market/summary',
  )

  if (isError) {
    return (
      <div className="card">
        <AnalyticsErrorBlock
          title="Failed to load market summary"
          onRetry={() => {
            void refetch()
          }}
        />
      </div>
    )
  }

  if (isLoading || !data) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <KPICardSkeleton key={i} />
        ))}
      </div>
    )
  }

  const cards: KPICardData[] = [
    {
      id: 'medianSqmPrice',
      label: 'Median €/m²',
      value: euroInt(data.medianSqmPrice),
      icon: Coins,
      color: 'purple',
      detail: 'Trimmed',
      info: {
        definition:
          'Middle value of €/m² across units currently on the market — the headline pricing benchmark for the filtered scope. Median is resistant to ultra-luxury and micro-unit distortion.',
        formula: 'PERCENTILE_CONT(0.5) WITHIN GROUP (ORDER BY current_price / private_gpa)',
        calculation:
          'Computed over available + reserved units with valid price and private area. Outliers outside the 500–25,000 €/m² band are trimmed by default; toggle Include outliers in the filter bar to bypass the trim. €/m² uses private_gpa as the canonical denominator across Blue Tagus.',
        source: 'unit.current_price / unit.private_gpa',
      },
    },
    {
      id: 'medianTimeOnMarket',
      label: 'Median days to sell',
      value: `${int(data.medianTimeOnMarket)}d`,
      icon: Clock,
      color: 'amber',
      detail: 'Sold in period',
      info: {
        definition:
          'Typical time a unit spent on the market before closing. Median is used (not average) to neutralise legacy stock outliers.',
        formula: 'MEDIAN(sales_date - listing_date) WHERE status = "sold"',
        calculation:
          'Limited to units with both listing_date and sales_date set. Scoped to the period selected in the filter bar.',
        source: 'unit.listing_date, unit.sales_date',
      },
    },
    {
      id: 'priceDrops',
      label: 'Price drops (30d)',
      value: int(data.priceDropsLast30d),
      icon: TrendingDown,
      color: 'red',
      detail: 'From unit_history',
      info: {
        definition:
          'Number of downward price revisions observed in the trailing 30 days — a momentum signal for demand weakness or repricing pressure.',
        formula:
          'COUNT(unit_history) WHERE field = "current_price" AND new_value < old_value AND changed_at > now() - 30d',
        calculation:
          'Each row in unit_history represents a captured change by the scraper. Multiple drops on the same unit are each counted. Reservations/sales are excluded — only price column changes are considered.',
        source: 'unit_history',
      },
    },
    {
      id: 'priceIncreases',
      label: 'Price increases (30d)',
      value: int(data.priceIncreasesLast30d),
      icon: TrendingUp,
      color: 'green',
      detail: 'From unit_history',
      info: {
        definition:
          'Number of upward price revisions observed in the trailing 30 days — a momentum signal for demand strength or scarcity pricing.',
        formula:
          'COUNT(unit_history) WHERE field = "current_price" AND new_value > old_value AND changed_at > now() - 30d',
        calculation:
          'Counts each price-up event captured in the audit trail. Useful to compare against price drops for a net-price-action indicator.',
        source: 'unit_history',
      },
    },
    {
      id: 'salesLast30d',
      label: 'Sales (30d)',
      value: int(data.salesLast30d),
      icon: CheckCircle,
      color: 'green',
      detail: 'Status → sold',
      info: {
        definition:
          'Units that transitioned to "sold" in the trailing 30 days — the short-term demand pulse.',
        formula: 'COUNT(unit) WHERE status = "sold" AND sales_date > now() - 30d',
        calculation:
          'Fixed 30-day window ending now, regardless of the global period filter. Ideal for quick momentum comparison with the 30d price action metrics above.',
        source: 'unit.status, unit.sales_date',
      },
    },
    {
      id: 'discount',
      label: 'Avg list→sale discount',
      value: pct(data.avgListToSaleDiscountPct),
      icon: Percent,
      color: 'blue',
      detail: 'Sold units',
      info: {
        definition:
          'Average percentage difference between a unit\'s first listed price and its final sale price. Negative values mean the unit sold below asking.',
        formula: 'AVG((sale_price - first_listed_price) / first_listed_price × 100)',
        calculation:
          'First listed price is pulled from the earliest unit_history entry; final sale price is the unit\'s current_price at sold time. Restricted to units with both values. Excludes units with missing history.',
        source: 'unit_history, unit.current_price',
      },
    },
  ]

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
      {cards.map((c) => (
        <KPICard key={c.id} card={c} />
      ))}
    </div>
  )
}

function PriceByCityChart() {
  const { data, isLoading, isError, refetch } = useAnalyticsQuery<PriceByCityRow[]>(
    'market-price-by-city',
    '/analytics/market/price-by-city',
  )
  const { toggleIn, filters } = useFilterDispatch()

  const handleBarClick = (payload: { city?: string } | undefined) => {
    if (payload?.city) toggleIn('cities', payload.city)
  }

  return (
    <ChartChrome
      title="€/m² by city"
      subtitle="Top 20 cities · trimmed avg €/m² · min 5 units · click a bar to filter"
      info={{
        data: {
          definition:
            'Average €/m² per city across currently-listed active units, ranked. Shows where the market is priced.',
          formula: 'AVG(current_price / private_gpa) GROUP BY city',
          calculation:
            'Includes available + reserved units with valid price and private area. Outliers (outside 500–25,000 €/m²) are trimmed by default; toggle Include outliers to bypass. Cities with fewer than 5 units are excluded. Top 20 cities by avg shown. €/m² uses private_gpa as the Blue Tagus canonical denominator.',
          source: 'unit.current_price, unit.private_gpa, project.city',
        },
      }}
      bodyMinHeight={420}
      render={(height) => {
        if (isError)
          return (
            <AnalyticsErrorBlock
              minHeight={height}
              onRetry={() => {
                void refetch()
              }}
            />
          )
        if (isLoading || !data) return <AnalyticsLoadingBlock height={height} />
        if (data.length === 0) return <AnalyticsEmptyState minHeight={height} />
        const chartHeight = Math.max(height, data.length * 26)
        return (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart data={data} layout="vertical" margin={{ left: 80, right: 16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `€${int(v)}`} />
              <YAxis
                type="category"
                dataKey="city"
                tick={{ fontSize: 11 }}
                width={120}
                interval={0}
              />
              <Tooltip
                formatter={(value: number, name: string) => {
                  if (name === 'units') return [int(value), 'Units']
                  return [euroInt(value), name === 'avgSqmPrice' ? 'Avg €/m²' : 'Median €/m²']
                }}
              />
              <Bar
                dataKey="avgSqmPrice"
                name="Avg €/m²"
                cursor="pointer"
                onClick={(entry) => handleBarClick(entry as { city?: string })}
              >
                {data.map((row) => (
                  <Cell
                    key={row.city}
                    fill={filters.cities.includes(row.city) ? '#1d4ed8' : '#2563eb'}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )
      }}
    />
  )
}

function PriceTrendChart() {
  const { data, isLoading, isError, refetch } = useAnalyticsQuery<PriceTrendSeries[]>(
    'market-price-trend',
    '/analytics/market/price-trend',
  )

  const mergedData = React.useMemo(() => {
    if (!data) return []
    const byMonth = new Map<string, Record<string, number | string>>()
    for (const series of data) {
      for (const p of series.points) {
        const row = byMonth.get(p.month) ?? { month: p.month }
        row[series.city] = p.avgSqmPrice
        byMonth.set(p.month, row)
      }
    }
    return Array.from(byMonth.values()).sort((a, b) =>
      String(a.month).localeCompare(String(b.month)),
    )
  }, [data])

  return (
    <ChartChrome
      title="€/m² trend (12m)"
      subtitle="Monthly avg — top 5 cities · sourced from unit_history (since 2025-10-06)"
      info={{
        data: {
          definition:
            'Rolling 12-month trajectory of average €/m² in the 5 most-active cities. Shows pricing momentum by micro-market.',
          formula: 'AVG(current_price / private_gpa) per month, per city',
          calculation:
            'Built from unit_history snapshots (price changes captured by the scrapers). Cities are the 5 with the most units in the current filtered scope. Only history rows from 2025-10-06 onward are available. Trimmed to 500–25,000 €/m² to remove outliers. €/m² uses private_gpa as the Blue Tagus canonical denominator.',
          source: 'unit_history.current_price, unit.private_gpa, project.city',
        },
      }}
      render={(height) => {
        if (isError)
          return (
            <AnalyticsErrorBlock
              minHeight={height}
              onRetry={() => {
                void refetch()
              }}
            />
          )
        if (isLoading || !data) return <AnalyticsLoadingBlock height={height} />
        if (mergedData.length === 0)
          return (
            <AnalyticsEmptyState
              minHeight={height}
              title="No trend data yet for current filters"
              hint="Price history starts 2025-10-06. Widen the period or add more cities."
            />
          )
        return (
          <ResponsiveContainer width="100%" height={height}>
            <LineChart data={mergedData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `€${int(v)}`} />
              <Tooltip formatter={(value: number) => [euroInt(value), 'Avg €/m²']} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {data.map((series, idx) => (
                <Line
                  key={series.city}
                  type="monotone"
                  dataKey={series.city}
                  stroke={TREND_COLORS[idx % TREND_COLORS.length]}
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )
      }}
    />
  )
}

function PriceDistributionChart() {
  const { data, isLoading, isError, refetch } = useAnalyticsQuery<PriceDistributionResponse>(
    'market-price-distribution',
    '/analytics/market/price-distribution',
  )
  const { setRange, filters } = useFilterDispatch()

  const chartData = React.useMemo(() => {
    if (!data) return []
    return data.buckets.map((b) => ({
      label: `€${int(b.lower)}`,
      lower: b.lower,
      upper: b.upper,
      count: b.count,
    }))
  }, [data])

  const isBucketActive = (lower: number, upper: number) =>
    filters.min_price !== null &&
    filters.max_price !== null &&
    filters.min_price === Math.round(lower) &&
    filters.max_price === Math.round(upper)

  const handleBucketClick = (
    payload: { lower?: number; upper?: number } | undefined,
  ) => {
    if (payload?.lower === undefined || payload?.upper === undefined) return
    const lower = Math.round(payload.lower)
    const upper = Math.round(payload.upper)
    if (isBucketActive(payload.lower, payload.upper)) {
      setRange('min_price', 'max_price', null, null)
    } else {
      setRange('min_price', 'max_price', lower, upper)
    }
  }

  const subtitle = (
    <>
      20-bucket histogram · €/m² · click a bucket to filter price range
      {data
        ? ` · median ${euroInt(data.median)} · Q1 ${euroInt(data.q1)} · Q3 ${euroInt(data.q3)}`
        : ''}
      {data && data.trimmedCount > 0 ? ` · ${int(data.trimmedCount)} outliers trimmed` : ''}
    </>
  )

  return (
    <ChartChrome
      title="Price distribution"
      subtitle={subtitle}
      info={{
        data: {
          definition:
            'Unit count by €/m² bucket across the filtered live inventory. Reveals pricing concentration and fat tails.',
          formula:
            'COUNT(unit) grouped into 20 equal-width buckets over [min, max] of current_price/private_gpa',
          calculation:
            'Bucket width = (max − min) / 20 on the trimmed €/m² distribution. Outliers outside 500–25,000 €/m² are excluded by default (toggle via filter bar). Median, Q1, Q3 annotations are computed on the same distribution. €/m² uses private_gpa as the Blue Tagus canonical denominator.',
          source: 'unit.current_price, unit.private_gpa',
        },
      }}
      render={(height) => {
        if (isError)
          return (
            <AnalyticsErrorBlock
              minHeight={height}
              onRetry={() => {
                void refetch()
              }}
            />
          )
        if (isLoading || !data) return <AnalyticsLoadingBlock height={height} />
        if (chartData.length === 0) return <AnalyticsEmptyState minHeight={height} />
        return (
          <ResponsiveContainer width="100%" height={height}>
            <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={1} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip
                formatter={(value: number) => [int(value), 'Units']}
                labelFormatter={(label, payload) => {
                  const p = payload?.[0]?.payload
                  if (!p) return label as string
                  return `${euroInt(p.lower)} – ${euroInt(p.upper)}`
                }}
              />
              <Bar
                dataKey="count"
                name="Units"
                cursor="pointer"
                onClick={(entry) =>
                  handleBucketClick(entry as { lower?: number; upper?: number })
                }
              >
                {chartData.map((bucket, idx) => (
                  <Cell
                    key={idx}
                    fill={isBucketActive(bucket.lower, bucket.upper) ? '#6d28d9' : '#8b5cf6'}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )
      }}
    />
  )
}

function TypologyMixChart() {
  const { data, isLoading, isError, refetch } = useAnalyticsQuery<TypologyMixRow[]>(
    'market-typology-mix',
    '/analytics/market/typology-mix',
  )
  const { toggleIn, filters } = useFilterDispatch()

  const handleSliceClick = (entry: { typology?: string } | undefined) => {
    if (entry?.typology) toggleIn('typologies', entry.typology)
  }

  return (
    <ChartChrome
      title="Typology mix"
      subtitle="Active stock (available + reserved) · click a slice to filter typology"
      info={{
        data: {
          definition:
            'Share of live inventory by typology (T0/T1/T2/…). Shows the supply composition of the filtered market.',
          formula: 'COUNT(unit) GROUP BY typology WHERE status IN (available, reserved)',
          calculation:
            'Active stock only — sold units excluded. Typologies are normalised by the DB trigger (T0..T5+, V0..V5+, lofts/studios → T0). Duplex/Triplex and "+1" suffixes collapse to base room count.',
          source: 'unit.typology, unit.status',
        },
      }}
      render={(height) => {
        if (isError)
          return (
            <AnalyticsErrorBlock
              minHeight={height}
              onRetry={() => {
                void refetch()
              }}
            />
          )
        if (isLoading || !data) return <AnalyticsLoadingBlock height={height} />
        if (data.length === 0) return <AnalyticsEmptyState minHeight={height} />
        return (
          <ResponsiveContainer width="100%" height={height}>
            <PieChart>
              <Pie
                data={data}
                dataKey="units"
                nameKey="typology"
                cx="50%"
                cy="50%"
                outerRadius={Math.min(height / 2.6, 220)}
                label={(entry) =>
                  `${entry.typology} (${(entry.pct as number).toFixed(0)}%)`
                }
                labelLine={false}
                cursor="pointer"
                onClick={(entry) =>
                  handleSliceClick(entry as { typology?: string })
                }
              >
                {data.map((row, idx) => {
                  const active = filters.typologies.includes(row.typology)
                  return (
                    <Cell
                      key={idx}
                      fill={TYPOLOGY_COLORS[idx % TYPOLOGY_COLORS.length]}
                      stroke={active ? '#0f172a' : '#fff'}
                      strokeWidth={active ? 3 : 1}
                    />
                  )
                })}
              </Pie>
              <Tooltip
                formatter={(value: number, _name, item) => {
                  const row = item?.payload as TypologyMixRow | undefined
                  return [
                    `${int(value)} units${row ? ` (${row.pct.toFixed(1)}%)` : ''}`,
                    row?.typology ?? '',
                  ]
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        )
      }}
    />
  )
}

function HeatmapSection() {
  const { data, isLoading, isError, refetch } = useAnalyticsQuery<HeatmapPoint[]>(
    'market-heatmap',
    '/analytics/market/heatmap',
  )
  const { toggleIn, filters } = useFilterDispatch()

  return (
    <ChartChrome
      title="Price heatmap"
      subtitle="City-aggregated · weight = avg €/m² · click a city marker to filter · tiles via OpenStreetMap / CARTO"
      info={{
        data: {
          definition:
            'Geographic concentration of pricing pressure: each marker is a city centroid weighted by its avg €/m² and sized by unit count.',
          formula:
            'median(project.latitude), median(project.longitude), AVG(sqm_price), COUNT(*) GROUP BY city',
          calculation:
            'Centroid uses median (not average) of project coordinates to neutralise geocoding outliers — e.g. a sales-office address mistakenly geocoded outside the actual city. A Portugal-mainland + islands bounding box drops obviously wrong coordinates upstream. Cities with fewer than 3 units are hidden; top 200 returned.',
          source: 'project.latitude, project.longitude, project.city, unit.sqm_price',
        },
      }}
      bodyMinHeight={420}
      render={(height) => {
        if (isError)
          return (
            <AnalyticsErrorBlock
              minHeight={height}
              title="Failed to load heatmap"
              onRetry={() => {
                void refetch()
              }}
            />
          )
        if (isLoading || !data) return <AnalyticsLoadingBlock height={height} />
        if (data.length === 0)
          return (
            <AnalyticsEmptyState
              minHeight={height}
              title="No geocoded data for current filters"
            />
          )
        return (
          <HeatmapLeafletMap
            points={data}
            selectedCities={filters.cities}
            onCityClick={(city) => toggleIn('cities', city)}
            height={height}
          />
        )
      }}
    />
  )
}

function MarketBody() {
  const { queryParams } = useAnalyticsFilters()
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end">
        <ExportButton
          endpoint="/analytics/export/market.csv"
          params={queryParams}
          filename="analytics-market"
        />
      </div>
      <SummaryRow />
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <PriceByCityChart />
        <PriceTrendChart />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <PriceDistributionChart />
        <TypologyMixChart />
      </div>
      <HeatmapSection />
    </div>
  )
}

export function MarketAnalytics() {
  return (
    <AnalyticsLayout>
      <MarketBody />
    </AnalyticsLayout>
  )
}
